import axios, {AxiosInstance} from 'axios'
import {threadType} from '../types/thread'
import {messageType} from '../types/message'
import MyAxiosInstance from './axiosInstance'
import {AssistantType} from '../types/assistant'
import {IUserInfo} from '../types/user'
import {limitsType} from '../types/limits'
import {subscription} from '../types/subscription'
import {Organization} from '../types/orgUsers'
import {OrgMetrics} from '../types/orgUsersMetrics'
import {IResponsePresignedUrl} from '../types/preSigned'
import {ModuleReport} from '../types/moduleReport'

const S3_PUT_MAX_ATTEMPTS = 3
const S3_PUT_BACKOFF_MS = 1000

// Falha transitória: sem resposta (rede/timeout), 429 ou 5xx. 403 (URL
// expirada/assinatura) e demais 4xx não se resolvem repetindo.
const isTransientError = (error: unknown) => {
    if (!axios.isAxiosError(error)) return false
    const status = error.response?.status
    return !status || status === 429 || status >= 500
}

export class AssistantApi {
    post(
        arg0: string,
        projectData: {
            created_at: string
            name: string
            description: string
            updated_at: string
            start_date: string
            end_date: string
            is_active: boolean
            project_type_id: string
            status: string
        }
    ) {
        throw new Error('Method not implemented.')
    }
    private api: AxiosInstance

    client = axios.create({
        baseURL: import.meta.env.VITE_DOCUMENT_URL,
    })

    constructor() {
        this.api = MyAxiosInstance
    }

    async getAssistants(): Promise<AssistantType[]> {
        try {
            const res: {data: AssistantType[]} =
                await this.api.get('/assistants')
            return res.data
        } catch (error) {
            console.log(error)
            const pattern: AssistantType[] = []
            return pattern
        }
    }

    async getThreads(limit = 15, skip = 0): Promise<threadType[]> {
        try {
            const res: {data: threadType[]} = await this.api.get(
                `/threads?limit=${limit}&skip=${skip}`
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async unarchiveThread(threadId: string): Promise<void> {
        try {
            await this.api.put(`/threads/${threadId}/restore`)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getThread(thread_id: string): Promise<threadType> {
        try {
            const res: {data: threadType} = await this.api.get(
                `/threads/${thread_id}`
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getThreadsArchived(limit = 15, skip = 0): Promise<threadType[]> {
        try {
            const res: {data: threadType[]} = await this.api.get(
                `/threads/archived?limit=${limit}&skip=${skip}`
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getMessages(thread_id: string): Promise<messageType[]> {
        try {
            const res: {data: messageType[]} = await this.api.get(
                `/threads/${thread_id}/messages`
            )
            return res.data.reverse()
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async sendMessage(
        thread_id: string,
        content: string,
        assistant_id: string,
        author_id: string,
        document_ids?: string[],
        guid?: string
    ): Promise<void> {
        try {
            await this.api.post('/messages/v2', {
                ...(guid && {guid}),
                content,
                thread_id: thread_id === 'new' ? null : thread_id,
                assistant_id,
                author_id,
                document_ids: document_ids || [],
            })
        } catch (error) {
            console.log(error)
        }
    }

    async regenerateAnswer(thread_id: string): Promise<void> {
        try {
            await this.api.put(`/threads/${thread_id}/regenerate`)
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getUser(): Promise<IUserInfo> {
        try {
            const res: {data: IUserInfo} = await this.api.get(`/users`)
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async updateUser(user: IUserInfo): Promise<IUserInfo> {
        try {
            const res: {data: IUserInfo} = await this.api.put(
                `/users/data`,
                user
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async archiveThread(threadGuid: string): Promise<void> {
        try {
            await this.api.get(`/threads/${threadGuid}/archive`)
            return
        } catch (error) {
            console.log(error)
        }
    }

    async renameThread(threadGuid: string, name: string): Promise<threadType> {
        try {
            const res: {data: threadType} = await this.api.put(
                `/threads/${threadGuid}`,
                {name}
            )
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async deleteThread(threadGuid: string): Promise<void> {
        try {
            await this.api.delete(`/threads/${threadGuid}/`)
            return
        } catch (error) {
            console.log(error)
        }
    }

    async getConstraints(): Promise<{max_characters: number}> {
        try {
            const res = await this.api.get('/users/constraints')
            return res.data
        } catch (error) {
            console.error('Erro ao obter constraints:', error)
            throw 'Erro ao obter constraints da API.'
        }
    }

    async getCurrentSubscription(): Promise<subscription> {
        try {
            const res = await this.api.get('/subscriptions/current')
            return res.data
        } catch (error) {
            console.error('Erro ao obter subscription:', error)
            throw 'Erro ao obter subscription da API.'
        }
    }

    async getLimits(): Promise<limitsType> {
        try {
            const res: {data: limitsType} = await this.api.get(`/users/limits`)
            return res.data
        } catch (error) {
            console.log(error)
            const pattern: limitsType = {
                created_at: '',
                usages: {
                    created_at: '',
                    messages: {
                        max: 0,
                        current: 0,
                    },
                    projects: {
                        max: 0,
                        current: 0,
                    },
                },
            }
            return pattern
        }
    }

    async reportAnswer(content: string, answer_guid: string): Promise<void> {
        try {
            const res: {data: messageType} = await this.api.post(
                `/answers/report`,
                {content, answer_guid}
            )
            console.log('ok', res)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async reportModule(report: ModuleReport): Promise<void> {
        try {
            await this.api.post(`/reports/`, report)
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async confirmPurchase(CHECKOUT_SESSION_ID: string) {
        try {
            await this.api.get(`/subscriptions/${CHECKOUT_SESSION_ID}/handle`)
            return
        } catch (error) {
            console.log('confirm purchase: ', error)
            throw 403
        }
    }

    async getOrganizationUsers(): Promise<Organization[]> {
        try {
            const res = await this.api.get('/organizations')
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async getOrganizationUsersMetrics(): Promise<OrgMetrics> {
        try {
            const res = await this.api.get('/organizations/users')
            return res.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async setOrganizationMaxUsages(payload: {
        split_resources: boolean
        split_docs: boolean
        users_limits: {
            guid: string
            max_messages: number
            max_documents: number
        }[]
    }): Promise<void> {
        try {
            await this.api.post('/organizations/max-usages', payload)
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async editUserOrganization(
        user_guid: string,
        email: string,
        first_name: string,
        last_name: string,
        phone: string,
        password: string
    ): Promise<void> {
        try {
            await this.api.put(`/organizations/users/${user_guid}`, {
                email: email,
                first_name: first_name,
                last_name: last_name,
                phone: phone,
                password: password,
            })
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async inviteUserOrganization(
        email: string,
        first_name: string,
        last_name: string
    ): Promise<void> {
        try {
            await this.api.post(`/organizations/user/invite`, {
                email,
                first_name,
                last_name,
            })
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async removeUserOrganization(user_guid: string): Promise<void> {
        try {
            await this.api.delete(`/organizations/users/${user_guid}`)
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async setUserOrganization(organization_guid: string): Promise<void> {
        try {
            await this.api.put('/users/organization', {organization_guid})
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async UpdateUser(
        users_email?: string,
        users_first_name?: string,
        users_last_name?: string,
        users_password?: string,
        user_data_phone?: string,
        user_data_personal_id?: string,
        user_data_address?: string
    ): Promise<void> {
        try {
            await this.api.put(`/users/edit_user_data`, {
                users_email: users_email,
                users_first_name: users_first_name,
                users_last_name: users_last_name,
                users_password: users_password,
                user_data_phone: user_data_phone,
                user_data_personal_id: user_data_personal_id,
                user_data_address: user_data_address,
            })
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    /**
     * Pede as presigned URLs de todos os arquivos numa única chamada e sobe
     * cada um no S3 em paralelo. O resultado segue a ordem de `files`.
     */
    async uploadS3PresignedUrls(
        thread_id: string,
        user_id: string,
        organization_id: string,
        files: File[],
        assistant_id: string,
        message_guid?: string
    ): Promise<PromiseSettledResult<IResponsePresignedUrl>[]> {
        try {
            const tagging = `thread_id=${thread_id}&user_id=${user_id}&organization_id=${organization_id}&assistant_id=${assistant_id}`
            const response: {data: IResponsePresignedUrl[]} =
                await this.api.post(
                    `/threads/${thread_id}/pre_signed_url`,
                    files.map((file) => ({
                        file_name: `${file.name}`,
                        content_type: `${file.type}`,
                        ...(message_guid && {message_guid}),
                        tagging,
                    })),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        timeout: 60000
                    },
                )

            return Promise.allSettled(
                response.data.map(async (uploadData, index) => {
                    for (let attempt = 1; ; attempt++) {
                        try {
                            await axios.put(uploadData.url, files[index], {
                                headers: {
                                    'Content-Type': files[index].type,
                                    'x-amz-tagging': uploadData.tagging,
                                },
                                timeout: 60000,
                            })
                            return uploadData
                        } catch (error) {
                            if (
                                attempt >= S3_PUT_MAX_ATTEMPTS ||
                                !isTransientError(error)
                            )
                                throw error
                            await new Promise((resolve) =>
                                setTimeout(
                                    resolve,
                                    S3_PUT_BACKOFF_MS * 2 ** (attempt - 1)
                                )
                            )
                        }
                    }
                })
            )
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    /**
     * Pede ao core a URL pré-assinada (S3, 600s, Content-Disposition attachment)
     * do arquivo original anexado numa thread. O download em si é feito pelo
     * navegador ao navegar para `url` 
     */
    async downloadThreadDocument(
        thread_id: string,
        document_id: string
    ): Promise<{url: string; file_name: string}> {
        const response: {data: {url: string; file_name: string}} =
            await this.api.get(
                `/threads/${thread_id}/documents/${document_id}/download`
            )
        return response.data
    }

    async sendEmailSupport(
        email: string,
        category: string,
        subject: string,
        message: string,
        file?: File
    ): Promise<void> {
        try {
            const formData = new FormData()
            formData.append('email', email)
            formData.append('category', category)
            formData.append('subject', subject)
            formData.append('message', message)

            if (file) {
                formData.append('file', file)
            }

            await this.api.post('/support', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async setNewPassword(
        oldPassword: string,
        newPassword: string,
        confirmPassword: string
    ): Promise<void> {
        try {
            await this.api.put('/users/edit_current_password', {
                current_password: oldPassword,
                new_password: newPassword,
                new_password_confirmation: confirmPassword,
            })
            return
        } catch (error) {
            console.log(error)
            throw error
        }
    }
}
